/**
 * Résout une URL relative API (`/U3208/...`) vers l’URL absolue (VITE_API_URL).
 * BL-0219 — ne pas ouvrir la route JWT en onglet nu.
 */
export function u3204ResolveOrgCitationApiUrl(url: string, apiBase?: string): string {
  const base = (apiBase ?? import.meta.env.VITE_API_URL ?? '').replace(/\/$/, '');
  if (/^https?:\/\//i.test(url)) {
    return url;
  }
  if (url.startsWith('/')) {
    return `${base}${url}`;
  }
  return `${base}/${url}`;
}

export function u3204BuildOrgCitationFetchHeaders(
  resolvedUrl: string,
  token: string | null | undefined,
  apiBase?: string,
): Record<string, string> {
  const base = apiBase ?? import.meta.env.VITE_API_URL ?? '';
  const headers: Record<string, string> = {};
  const isApiRoute =
    resolvedUrl.startsWith(base) || resolvedUrl.startsWith('/') || Boolean(base && resolvedUrl.includes(base));
  if (isApiRoute && token) {
    headers.Authorization = `Bearer ${token}`;
  }
  if (base.includes('ngrok')) {
    headers['ngrok-skip-browser-warning'] = '1';
  }
  return headers;
}

export type U3204OpenOrgCitationSourceDeps = {
  fetchFn?: typeof fetch;
  createObjectUrl?: (blob: Blob) => string;
  revokeObjectUrl?: (url: string) => void;
  openWindow?: (url: string, target?: string, features?: string) => Window | null;
  scheduleRevoke?: (fn: () => void, ms: number) => void;
  apiBase?: string;
};

/**
 * Fetch JWT du fichier source org puis ouverture blob (viewer).
 * En cas d’échec HTTP / réseau : throw — pas de fallback URL nue.
 */
export async function u3204OpenOrgCitationSource(
  url: string,
  token: string | null | undefined,
  deps: U3204OpenOrgCitationSourceDeps = {},
): Promise<void> {
  const fetchFn = deps.fetchFn ?? fetch;
  const createObjectUrl = deps.createObjectUrl ?? ((blob: Blob) => URL.createObjectURL(blob));
  const revokeObjectUrl = deps.revokeObjectUrl ?? ((objectUrl: string) => URL.revokeObjectURL(objectUrl));
  const openWindow =
    deps.openWindow ??
    ((objectUrl: string, target?: string, features?: string) => window.open(objectUrl, target, features));
  const scheduleRevoke =
    deps.scheduleRevoke ?? ((fn: () => void, ms: number) => {
      window.setTimeout(fn, ms);
    });

  const resolvedUrl = u3204ResolveOrgCitationApiUrl(url, deps.apiBase);
  const response = await fetchFn(resolvedUrl, {
    method: 'GET',
    headers: u3204BuildOrgCitationFetchHeaders(resolvedUrl, token, deps.apiBase),
    credentials: 'include',
  });

  if (!response.ok) {
    throw new Error(`HTTP ${response.status}`);
  }

  const blob = await response.blob();
  const objectUrl = createObjectUrl(blob);
  try {
    const opened = openWindow(objectUrl, '_blank', 'noopener,noreferrer');
    if (!opened) {
      throw new Error('POPUP_BLOCKED');
    }
  } finally {
    // Révoquer après un court délai pour laisser le viewer charger le blob.
    scheduleRevoke(() => revokeObjectUrl(objectUrl), 60_000);
  }
}
